<script setup lang="ts">
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import PageLoading from "@/components/Common/PageLoading.vue";
import { toast } from "vue-sonner";
import { ItemDataResponse } from "@/types/interface/item";
import { mapOptionMultiselect } from "@/utils/helper";
import { useVesselStore } from "@/stores/master/vessel";
import { VesselDataResponse } from "@/types/interface/vessel";
import { VesselItemPayload } from "@/types/interface/item-vessel";
import { useItemStore } from "@/stores/management/item";
import { useVesselItemStore } from "@/stores/management/item-vessel";

const idItem = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const detailItem = ref<ItemDataResponse>();
const listVessel = ref<VesselDataResponse>();
const vesselStore = useVesselStore();
const itemStore = useItemStore();
const vesselItemStore = useVesselItemStore();
const listItems = ref([]);
const isVariant = ref(false);
const listVariant = ref([]);

const validationSchema = toTypedSchema(
  zod.object({
    id_vessel: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    id_goods: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    quantity: zod.number(),
    rob: zod.number(),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: id_vessel } = useField<number>("id_vessel");
const { value: id_goods } = useField<number>("id_goods");
const { value: id_variant } = useField<number>("id_variant");
const { value: quantity } = useField<number>("quantity");
const { value: rob } = useField<number>("rob");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;

  const payloadVesselItem: VesselItemPayload = {
    vessel_id: id_vessel.value?.value,
    goods_id: id_goods.value.value,
    variant_id: id_variant.value?.value,
    quantity: quantity.value,
    rob: rob.value,
  };

  const responseApi = ref<any>();

  if (isEdit.value) {
    responseApi.value = await vesselItemStore.dispatchUpdate(
      idItem.value,
      payloadVesselItem
    );
  } else {
    responseApi.value = await vesselItemStore.dispatchCreate(payloadVesselItem);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Vessel Item Successfully Updated"
          : "Vessel Item Successfully Added"
      );
      router.push("/management/vessel-item");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailItem = async () => {
  const { success, message } = await vesselItemStore.dispatchDetail(
    idItem.value
  );
  if (success) {
    detailItem.value = await vesselItemStore?.vesselItem?.data;
    if (detailItem.value.variant != null) {
      isVariant.value = true;
      id_variant.value = mapOptionMultiselect(
        detailItem.value?.variant,
        "id",
        "key",
        "value"
      );
    }
    id_vessel.value = mapOptionMultiselect(detailItem.value?.vessel);
    id_goods.value = mapOptionMultiselect(detailItem.value?.goods);
    quantity.value = detailItem.value?.quantity;
    rob.value = detailItem.value?.rob;
  }
};

const loadListItem = async () => {
  const { success } = await itemStore.dispatchGetItems(1, 99);
  if (success) {
    listItems.value = mapOptionMultiselect(itemStore.items.items);
  }
};

const loadListVessel = async () => {
  const { success } = await vesselStore.dispatchGetAll(1, 99);
  if (success) {
    listVessel.value = mapOptionMultiselect(vesselStore.vessels.items);
  }
};

const handleSelectGoods = async () => {
  let result = {};
  const { success } = await itemStore.dispatchDetail(id_goods.value?.value);
  if (success) {
    result = itemStore.item.data;
    if (result.variant.length > 0) {
      isVariant.value = true;
      listVariant.value = mapOptionMultiselect(
        result.variant,
        "id",
        "key",
        "value"
      );
    }
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idItem.value = router.currentRoute.value.params.id.toString();
    loadDetailItem();
  }
  loadListVessel();
  loadListItem();
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card card-with-loading bg-white border-0 rounded-3 mb-4">
        <PageLoading text="Processing Data..." v-if="isLoading" />
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Item</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/management/vessel-item"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Item
            </a>
          </div>
          <form @submit="onSubmit" class="row g-3 needs-validation form">
            <div class="row">
              <div class="col-lg-12">
                <div class="form-group">
                  <label for="id_vessel" class="form-label label text-secondary"
                    >Vesel <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <vSelect
                      :options="listVessel"
                      name="id_vessel"
                      v-model="id_vessel"
                      placeholder="Choose Vessel"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.id_vessel }}</span>
                </div>
              </div>
              <div class="col-lg-12">
                <div class="form-group">
                  <label for="id_goods" class="form-label label text-secondary"
                    >Item <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <vSelect
                      :options="listItems"
                      name="id_goods"
                      v-model="id_goods"
                      placeholder="Choose Item"
                      @close="handleSelectGoods"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.id_goods }}</span>
                </div>
              </div>
              <div class="col-lg-12" v-if="isVariant">
                <div class="form-group">
                  <label
                    for="id_variant"
                    class="form-label label text-secondary"
                    >Varian <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <vSelect
                      :options="listVariant"
                      name="id_variant"
                      v-model="id_variant"
                      placeholder="Choose Varian Item"
                    ></vSelect>
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="quantity" class="form-label label text-secondary"
                    >Max Quantity <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="quantity"
                      v-model="quantity"
                      placeholder="Max Quantity"
                      type="number"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.quantity }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="rob" class="form-label label text-secondary"
                    >ROB (Remaining On Board)
                    <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="rob"
                      v-model="rob"
                      placeholder="Remaining On Board"
                      type="number"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.rob }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
